import { Link } from 'wouter';
import { LayoutDashboard, Mail } from 'lucide-react';
import { PRICING_CONFIG } from '../../config/pricingConfig';
import { isLegalDocsPublished } from '../../config/legalConfig';

const MarketingFooter = ({ variant = 'light' }: { variant?: 'light' | 'dark' }) => {
    const isLight = variant === 'light';
    const showLegalLinks = isLegalDocsPublished();

    const bgClass = isLight
        ? 'bg-slate-900 border-slate-800 text-slate-300'
        : 'bg-slate-950 border-slate-900 text-slate-400';
    const textHover = 'hover:text-teal-400';
    const titleClass = 'text-white';

    return (
        <footer className={`py-14 border-t ${bgClass}`}>
            <div className="max-w-7xl mx-auto px-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-12">
                    {/* Brand Column */}
                    <div className="lg:col-span-2 space-y-4">
                        <Link href="/" className={`inline-flex items-center gap-2.5 font-extrabold text-xl ${titleClass}`}>
                            <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white shadow-sm">
                                <LayoutDashboard size={17} />
                            </div>
                            <span>BayWhiz</span>
                        </Link>
                        <p className="text-sm text-slate-400 leading-relaxed max-w-sm">
                            Warehouse dock scheduling software for growing receiving and distribution teams. Give carriers a simple way to request appointments, approve bookings, assign dock doors, and track check-ins and dwell time from one live schedule.
                        </p>
                        <div className="pt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-400">
                            <span>30-day full-access trial</span>
                            <span aria-hidden="true">·</span>
                            <span>No credit card required</span>
                            <span aria-hidden="true">·</span>
                            <span>Pricing in USD per facility</span>
                        </div>
                        <div className="pt-1">
                            <a
                                href={`mailto:${PRICING_CONFIG.supportEmail}`}
                                className="inline-flex items-center gap-2 text-sm font-medium text-teal-400 hover:text-teal-300 transition-colors"
                            >
                                <Mail size={15} />
                                {PRICING_CONFIG.supportEmail}
                            </a>
                        </div>
                    </div>

                    {/* Platform Column */}
                    <div>
                        <h4 className={`text-sm font-bold uppercase tracking-wider mb-4 ${titleClass}`}>Platform</h4>
                        <ul className="space-y-2.5 text-sm">
                            <li>
                                <Link href="/product" className={`transition-colors ${textHover}`}>
                                    Product Overview
                                </Link>
                            </li>
                            <li>
                                <Link href="/how-it-works" className={`transition-colors ${textHover}`}>
                                    How It Works
                                </Link>
                            </li>
                            <li>
                                <Link href="/solutions" className={`transition-colors ${textHover}`}>
                                    Solutions
                                </Link>
                            </li>
                            <li>
                                <Link href="/pricing" className={`transition-colors ${textHover}`}>
                                    Pricing
                                </Link>
                            </li>
                            <li>
                                <Link href="/faq" className={`transition-colors ${textHover}`}>
                                    FAQ
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Account & Portals Column */}
                    <div>
                        <h4 className={`text-sm font-bold uppercase tracking-wider mb-4 ${titleClass}`}>Portals &amp; Access</h4>
                        <ul className="space-y-2.5 text-sm">
                            <li>
                                <Link href="/request-trial" className="text-teal-400 hover:text-teal-300 font-semibold transition-colors">
                                    Request Free Trial
                                </Link>
                            </li>
                            <li>
                                <Link href="/login?mode=warehouse" className={`transition-colors ${textHover}`}>
                                    Warehouse Login
                                </Link>
                            </li>
                            <li>
                                <Link href="/carrier-login" className={`transition-colors ${textHover}`}>
                                    Carrier Portal Login
                                </Link>
                            </li>
                            <li>
                                <Link href="/carrier-signup" className={`transition-colors ${textHover}`}>
                                    Carrier Account Sign Up
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Support & Contact Column */}
                    <div>
                        <h4 className={`text-sm font-bold uppercase tracking-wider mb-4 ${titleClass}`}>Support</h4>
                        <ul className="space-y-2.5 text-sm">
                            <li>
                                <Link href="/contact" className={`transition-colors ${textHover}`}>
                                    Contact Us
                                </Link>
                            </li>
                            <li>
                                <a href={`mailto:${PRICING_CONFIG.supportEmail}`} className={`transition-colors ${textHover}`}>
                                    Email Support
                                </a>
                            </li>
                            <li>
                                <Link href="/faq" className={`transition-colors ${textHover}`}>
                                    Trial &amp; Billing FAQ
                                </Link>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="pt-8 border-t border-slate-800 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
                    <p>© {new Date().getFullYear()} BayWhiz. All rights reserved.</p>
                    <div className="flex flex-wrap items-center gap-6">
                        <Link href="/product" className={`transition-colors ${textHover}`}>
                            Product
                        </Link>
                        <Link href="/pricing" className={`transition-colors ${textHover}`}>
                            Pricing
                        </Link>
                        <Link href="/faq" className={`transition-colors ${textHover}`}>
                            FAQ
                        </Link>
                        <Link href="/contact" className={`transition-colors ${textHover}`}>
                            Contact
                        </Link>
                        {showLegalLinks && (
                            <>
                                <Link
                                    href="/privacy"
                                    data-testid="footer-privacy-link"
                                    className={`transition-colors ${textHover}`}
                                >
                                    Privacy Policy
                                </Link>
                                <Link
                                    href="/terms"
                                    data-testid="footer-terms-link"
                                    className={`transition-colors ${textHover}`}
                                >
                                    Terms of Service
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default MarketingFooter;
